import { InteractivityChecker } from '@angular/cdk/a11y';
import { Injectable, InjectionToken, Injector, Type, ViewContainerRef } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { SabhaDialogConfig } from './dialog-config';
import { SabhaDialogContainer } from './dialog-container/dialog-container.component';
import { SabhaDialogRef } from './dialog-ref';

export const SABHA_DIALOG_REF: InjectionToken<SabhaDialogRef> = new InjectionToken<SabhaDialogRef>('SabhaDialogRef');
export const SABHA_DIALOG_DATA: InjectionToken<any> = new InjectionToken<any>('SabhaDialogData');
export const SABHA_DIALOG_CONFIG: InjectionToken<SabhaDialogConfig> = new InjectionToken<SabhaDialogConfig>(
  'SabhaDialogConfig'
);

@Injectable({
  providedIn: 'root',
})
export class SabhaDialogService {
  public viewContainerRef$ = new ReplaySubject<ViewContainerRef>(1);

  constructor(private _checker: InteractivityChecker) {}

  public registerDialogContainer(viewContainerRef: ViewContainerRef) {
    this.viewContainerRef$.next(viewContainerRef);
  }

  public unregisterDialogContainer(_: ViewContainerRef) {
    this.viewContainerRef$ = new ReplaySubject<ViewContainerRef>(1);
  }

  public open<T>(dialogComponent: Type<T>, config: SabhaDialogConfig, index: number = null): SabhaDialogRef {
    // Dialog reference object
    const dialogRef = new SabhaDialogRef(config);

    this.viewContainerRef$.pipe(take(1)).subscribe({
      next: viewContainerRef => {
        // Create an injector for the container component
        const containerInjector = Injector.create({
          providers: [
            { provide: SABHA_DIALOG_REF, useValue: dialogRef },
            { provide: SABHA_DIALOG_CONFIG, useValue: config },
          ],
          name: 'SabhaDialogConfigInjector',
        });
        // Add the container to view container passed
        const containerRef = viewContainerRef.createComponent(SabhaDialogContainer, {
          index,
          injector: containerInjector,
        });

        // Add container reference to dialog reference object
        dialogRef.containerRef = containerRef;

        // Trigger change detection to prevent ExpressionChangedAfterItHasBeenCheckedError
        containerRef.instance.triggerChangeDetection(); 
        
        // Subscribe to container loaded event
        containerRef.instance.afterLoaded$.subscribe((containerLoaded: boolean) => {
          if (containerLoaded) {
            // Create an injector for the dialog component
            const dataInjector = Injector.create({
              providers: [
                { provide: SABHA_DIALOG_REF, useValue: dialogRef },
                { provide: SABHA_DIALOG_DATA, useValue: config.data },
              ],
              name: 'SabhaDialogDataInjector',
            });
            // Add the dialog to container
            const dialogContainerRef = containerRef.instance.dialogContainer;
            const dialogElement = dialogContainerRef.createComponent(dialogComponent, { injector: dataInjector })
              .location.nativeElement as HTMLElement;

            // Auto focus on the first tabbable element
            if (config.autoFocus) {
              setTimeout(() => {
                const focusElement = this._getFirstTabbableElement(dialogElement);
                if (focusElement) {
                  focusElement.focus();
                }
              });
            }
          }
        });
      },
    });

    // Return dialog reference
    return dialogRef;
  }

  private _getFirstTabbableElement(root: HTMLElement): HTMLElement {
    if (this._checker.isFocusable(root) && this._checker.isTabbable(root)) {
      return root;
    }

    // Iterate in DOM order.
    let childCount = root.children.length;
    for (let i = 0; i < childCount; i++) {
      let tabbableChild = this._getFirstTabbableElement(root.children[i] as HTMLElement);
      if (tabbableChild) {
        return tabbableChild;
      }
    }

    return null;
  }
}
